import { Decoration } from 'prosemirror-view'

import {
  DEFAULT_SCENE_CONTD_FOOTER_PREFIX,
  DEFAULT_SCENE_CONTD_HEADER_PREFIX,
} from '#util/constants'
import { MoresContds } from '#util/formats'
import {
  HeaderOrFooterPrefs,
  SlotConfig,
  StaticSlotContentType,
} from '#util/zodSchemas/printPreferences'

import type { Revision, RevisionSet } from '../revisions'

import styles from './widgets.module.css'

export const PAGE_WIDGET_ORDER = {
  ensureNewline: 0,
  bottomOfPage: 1,
  topOfPage: 2,
  revisionRail: 3,
}

export interface PageData {
  pageNumber?: number
  pageAlias?: string
  pageSection?: string
  sceneContdHeader?: boolean
  sceneContdFooter?: boolean
  sceneNumber?: string
  sceneElementNumber?: string
  latestRevision?: RevisionSet
  isTitlePage?: boolean
}

export interface ScriptData {
  title?: string
  pages: number
  timestamp?: string
  date?: string
  duration?: string
  activeRevision?: Revision
}

export interface PageLayout {
  headers: HeaderOrFooterPrefs
  footers: HeaderOrFooterPrefs
}

export interface PageDecorationCallbacks {
  getSpeaker?: (pos: number | undefined) => string | undefined
}

export interface PageDecorationContext {
  page?: PageData
  script?: ScriptData
  layout?: PageLayout
  moresContds?: MoresContds
  callbacks?: PageDecorationCallbacks
}

// -----------------------------------------------------------------------------
// widget builder APIs
// -----------------------------------------------------------------------------

export const pageHeaderWidget = (
  pos: number,
  ctx: PageDecorationContext,
  opts: {
    isManualBreak: boolean
    isLockedPageBreak?: boolean
    continuationText?: string
    contdKey?: string
  },
): Decoration => {
  return Decoration.widget(pos, () => buildPageHeader(ctx, opts), {
    side: PAGE_WIDGET_ORDER.topOfPage,
    ...(opts.contdKey !== undefined && { relaxedSide: true }),
    key: opts.contdKey
      ? `${opts.contdKey}:top:${opts.isLockedPageBreak ? 'locked' : 'unlocked'}:${ctx.page?.pageAlias ?? ''}`
      : headerKey(ctx, opts),
  })
}

export const pageFooterWidget = (
  pos: number,
  ctx: PageDecorationContext,
  heightShortfall: number,
  more?: { text: string; key: string },
): Decoration => {
  return Decoration.widget(
    pos,
    () => buildPageFooter(ctx, heightShortfall, more?.text),
    {
      side: more
        ? PAGE_WIDGET_ORDER.bottomOfPage - 1
        : PAGE_WIDGET_ORDER.bottomOfPage,
      ...(more && { relaxedSide: true }),
      key: more
        ? `${more.key}:bottom:shortfall:${heightShortfall}`
        : footerKey(ctx, heightShortfall),
    },
  )
}

/**
 * Create a widget to represent what color a page should be when it belongs to a
 * revision. Draws two small "rails" bordering the page on the left and right
 */
export function revisionRailWidget(pos: number, revision: RevisionSet) {
  const rail = el('div', styles['revision-page-rails'])
  rail.style.setProperty('--revision-rail-color', revision.pageColor)

  return Decoration.widget(pos, rail, {
    kind: 'revision-rail',
    side: PAGE_WIDGET_ORDER.revisionRail,
    key: revision.pageColor,
  })
}

export function inlinePageBreakWidget(
  pos: number,
  footerCtx: PageDecorationContext,
  heightShortfall: number,
  headerCtx: PageDecorationContext,
  moreText: string | undefined,
  contdText: string | undefined,
  key: string,
): Decoration {
  return Decoration.widget(
    pos,
    () => {
      const wrapper = el(
        'div',
        styles[
          moreText || contdText ? 'dialogue-page-break' : 'inline-page-break'
        ],
      )
      wrapper.dataset.relaxedSide = 'true'
      wrapper.append(
        buildPageFooter(footerCtx, heightShortfall, moreText),
        buildPageHeader(headerCtx, {
          isManualBreak: false,
          continuationText: contdText,
        }),
      )
      return wrapper
    },
    { side: 1, relaxedSide: true, key: `${key}:shortfall:${heightShortfall}` },
  )
}

// -----------------------------------------------------------------------------
// Core page element builders
// -----------------------------------------------------------------------------

function buildPageHeader(
  ctx: PageDecorationContext,
  opts: {
    isManualBreak: boolean
    isLockedPageBreak?: boolean
    continuationText?: string
  },
): HTMLElement {
  const elt = el('div', 'top-of-page')
  if (opts.isManualBreak) elt.classList.add('manual-break')
  if (opts.isLockedPageBreak) elt.appendChild(buildPageLockIcon())
  if (ctx.page && ctx.script) {
    renderHeader(elt, ctx, opts.continuationText)
  } else if (opts.continuationText) {
    elt.appendChild(buildHeaderContinuationMarker(opts.continuationText))
  }
  return elt
}

function buildPageLockIcon(): HTMLElement {
  const icon = el('span', 'page-lock-icon', styles['page-lock-icon'])
  icon.setAttribute('aria-hidden', 'true')
  return icon
}

function buildFooterContinuationMarker(text: string): HTMLElement {
  const marker = el('div', styles['footer-continuation-marker'])
  marker.dataset.relaxedSide = 'true'
  marker.appendChild(document.createTextNode(text))
  return marker
}

function buildHeaderContinuationMarker(text: string = ' '): HTMLElement {
  const marker = el('div', styles['header-continuation-marker'])
  marker.dataset.relaxedSide = 'true'
  marker.appendChild(document.createTextNode(text.toUpperCase()))
  return marker
}

function buildPageFooter(
  ctx: PageDecorationContext,
  heightShortfall: number,
  continuationText?: string,
): HTMLElement {
  const elt = el('div', 'bottom-of-page', styles['bottom-of-page'])
  elt.style.setProperty('--page-height-shortfall', `${heightShortfall}em`)

  const footer = buildFooterContent(ctx, continuationText)
  if (footer) elt.appendChild(footer)

  return elt
}

// -----------------------------------------------------------------------------
// Decoration key builders
// -----------------------------------------------------------------------------

function headerKey(
  ctx: PageDecorationContext,
  opts: { isManualBreak: boolean; isLockedPageBreak?: boolean },
): string {
  const { page, script, layout, moresContds } = ctx
  const slots = layout?.headers?.slots
  const fullTitle = hasSlot('full-title', slots)
  const shortTitle = hasSlot('short-title', slots)
  return JSON.stringify({
    position: 'top',
    page: page?.pageAlias ?? page?.pageNumber ?? '',
    pages: script?.pages,
    isManual: opts.isManualBreak,
    isLocked: opts.isLockedPageBreak,
    headers: layout?.headers,
    sceneContd: moresContds?.enableSceneContds && page?.sceneContdHeader,
    sceneNumber: page?.sceneNumber,
    sceneElementNumber: page?.sceneElementNumber,
    revision: revisionKey(ctx),
    scriptTitle: fullTitle || shortTitle ? script?.title : undefined,
    pageSection: fullTitle ? page?.pageSection : undefined,
  })
}

function footerKey(
  ctx: PageDecorationContext,
  heightShortfall: number,
): string {
  const { page, script, layout, moresContds } = ctx
  const slots = layout?.footers?.slots
  const fullTitle = hasSlot('full-title', slots)
  const shortTitle = hasSlot('short-title', slots)
  return JSON.stringify({
    position: 'bottom',
    heightShortfall,
    page: page?.pageAlias ?? page?.pageNumber ?? '',
    pages: script?.pages,
    footers: layout?.footers,
    sceneContd: moresContds?.enableSceneContds && page?.sceneContdFooter,
    revision: revisionKey(ctx),
    scriptTitle: fullTitle || shortTitle ? script?.title : undefined,
    pageSection: fullTitle ? page?.pageSection : undefined,
  })
}

function revisionKey(ctx: PageDecorationContext) {
  return {
    latestRevision: ctx.page?.latestRevision?.label,
    activeRevision: ctx.script?.activeRevision?.label,
  }
}

// -----------------------------------------------------------------------------
// Header rendering
// -----------------------------------------------------------------------------

function renderHeader(
  elt: HTMLElement,
  ctx: PageDecorationContext,
  continuationText?: string,
): void {
  const { page } = ctx
  if (!page) return

  assignPageId(elt, page)

  const headerArea = buildHeaderArea(ctx, continuationText)
  if (headerArea) elt.appendChild(headerArea)

  applyInlinePageNumber(elt, ctx)
}

function assignPageId(elt: HTMLElement, page: PageData): void {
  const { isTitlePage = false, pageNumber } = page
  elt.id = [isTitlePage ? 'title-page' : 'page', pageNumber].join('-')
}

function buildHeaderArea(
  ctx: PageDecorationContext,
  continuationText?: string,
): HTMLElement | null {
  const { page, layout, moresContds } = ctx
  if (!page) return null

  const headerVisible = isPageHeaderVisible(page, layout)

  const wrapper = el('div', styles['page-header-wrapper'])
  if (headerVisible && layout?.headers) {
    wrapper.appendChild(buildSlotGrid('header', layout.headers, ctx))
  }
  wrapper.appendChild(buildSceneContdHeader(page, moresContds))
  wrapper.appendChild(buildHeaderContinuationMarker(continuationText))
  return wrapper
}

function isSlotAreaVisible(
  page: PageData,
  prefs?: HeaderOrFooterPrefs,
): boolean {
  if (prefs?.enabled === false) return false
  if (page.pageNumber === 1 && prefs?.showOnFirstPage !== true) return false
  return true
}

function isPageHeaderVisible(
  page: PageData,
  layout: PageDecorationContext['layout'],
): boolean {
  return isSlotAreaVisible(page, layout?.headers)
}

function isPageFooterVisible(
  page: PageData,
  layout: PageDecorationContext['layout'],
): boolean {
  return isSlotAreaVisible(page, layout?.footers)
}

function buildSceneContdHeader(
  page: PageData,
  moresContds: PageDecorationContext['moresContds'],
): HTMLElement {
  const elt = el('div', styles['scene-contd-header'])
  if (!moresContds?.enableSceneContds || !page.sceneContdHeader) return elt

  let text = DEFAULT_SCENE_CONTD_HEADER_PREFIX
  if (page.sceneNumber) text += ` (${page.sceneNumber})`
  elt.textContent = text

  if (page.sceneElementNumber) {
    const left = el('span', styles['scene-contd-header-left-number'])
    left.textContent = page.sceneElementNumber
    elt.appendChild(left)

    const right = el('span', styles['scene-contd-header-right-number'])
    right.textContent = page.sceneElementNumber
    elt.appendChild(right)
  }

  return elt
}

function applyInlinePageNumber(
  elt: HTMLElement,
  ctx: PageDecorationContext,
): void {
  const { page, layout } = ctx
  if (!page) return
  const { pageAlias, pageNumber, isTitlePage = false } = page
  const headersEnabled = layout?.headers?.enabled !== false
  if (headersEnabled || isTitlePage) return

  const displayPageNumber = pageAlias ?? pageNumber
  if (displayPageNumber !== undefined) {
    elt.style.setProperty('--page-number', `"${displayPageNumber}"`)
  }
}

// -----------------------------------------------------------------------------
// Footer rendering
// -----------------------------------------------------------------------------

function buildFooterContent(
  ctx: PageDecorationContext,
  continuationText?: string,
): HTMLElement | null {
  const { page, layout } = ctx
  if (!page) return null

  const visibleFooter = isPageFooterVisible(page, layout)

  const wrapper = el('div', styles['page-footer-wrapper'])
  // always render the continuation text, even if footers are disabled,
  // to ensure the correct height is reserved for the footer area
  wrapper.appendChild(buildFooterContinuationMarker(continuationText ?? ' '))
  const sceneContd = el('div', styles['scene-contd-footer'])
  if (ctx.moresContds?.enableSceneContds && page.sceneContdFooter) {
    sceneContd.textContent = DEFAULT_SCENE_CONTD_FOOTER_PREFIX
  }
  wrapper.appendChild(sceneContd)

  if (visibleFooter && layout?.footers) {
    wrapper.appendChild(buildSlotGrid('footer', layout.footers, ctx))
  }

  return wrapper
}

// -----------------------------------------------------------------------------
// Shared slot rendering
// -----------------------------------------------------------------------------
// a slot is a user-configurable content element that can be placed in headers or footers

function buildSlotGrid(
  prefix: 'header' | 'footer',
  prefs: HeaderOrFooterPrefs,
  ctx: PageDecorationContext,
): HTMLElement {
  const container = el('div', styles[`page-${prefix}`])
  if (prefs.divider) container.setAttribute('data-divider', 'true')
  for (const pos of ['left', 'center', 'right'] as const) {
    const slot = prefs.slots?.[pos]
    if (!slot) continue
    const slotElement = el('div', styles[`${prefix}-${pos}`])
    slotElement.textContent = renderSlot(slot, ctx)
    container.appendChild(slotElement)
  }
  return container
}

function renderSlot(
  slotConfig: SlotConfig,
  ctx: PageDecorationContext,
): string {
  const { page, script } = ctx
  if (slotConfig.slotType === 'literal') return slotConfig.text
  if (slotConfig.slotType === 'rundown-cell-value') return 'rundown-cell-value'
  if (slotConfig.slotType !== 'static') return ''

  switch (slotConfig.contentType) {
    case 'revisions':
      return script?.activeRevision?.label ?? ''
    case 'revisions-collated':
      return page?.latestRevision?.label ?? ''
    case 'page-number':
      return `${page?.pageAlias ?? page?.pageNumber ?? ''}.`
    case 'page-number-total':
      return `Page ${page?.pageNumber}/${script?.pages}`
    case 'short-title':
      return script?.title ?? slotConfig.contentType
    case 'full-title':
      return [
        script?.title ?? 'short-title',
        page?.pageSection?.toUpperCase(),
      ].join('\n')
    case 'timestamp':
      return script?.timestamp || ''
    case 'date':
      return script?.date || ''
    case 'duration':
      return script?.duration || ''
  }
}

function hasSlot(
  slotType: StaticSlotContentType,
  configuredSlots?: HeaderOrFooterPrefs['slots'],
): boolean {
  if (!configuredSlots) return false
  return Object.values(configuredSlots).some(
    (slot) => slot?.slotType === 'static' && slot.contentType === slotType,
  )
}

// -----------------------------------------------------------------------------
// DOM helpers
// -----------------------------------------------------------------------------

function el<T extends keyof HTMLElementTagNameMap>(
  tag: T,
  ...classNames: string[]
): HTMLElementTagNameMap[T] {
  const element = document.createElement(tag)
  if (classNames.length) element.className = classNames.join(' ')
  return element
}
